import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowRightLeft, History, Star, Trash2, TrendingUp, Maximize2, X } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/SearchableSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currency data with country names and flag emojis
const CURRENCY_DATA = {
  USD: { name: "United States", flag: "🇺🇸" },
  EUR: { name: "European Union", flag: "🇪🇺" },
  GBP: { name: "United Kingdom", flag: "🇬🇧" },
  JPY: { name: "Japan", flag: "🇯🇵" },
  AUD: { name: "Australia", flag: "🇦🇺" },
  CAD: { name: "Canada", flag: "🇨🇦" },
  CHF: { name: "Switzerland", flag: "🇨🇭" },
  CNY: { name: "China", flag: "🇨🇳" },
  HKD: { name: "Hong Kong", flag: "🇭🇰" },
  NZD: { name: "New Zealand", flag: "🇳🇿" },
  SEK: { name: "Sweden", flag: "🇸🇪" },
  KRW: { name: "South Korea", flag: "🇰🇷" },
  SGD: { name: "Singapore", flag: "🇸🇬" },
  NOK: { name: "Norway", flag: "🇳🇴" },
  MXN: { name: "Mexico", flag: "🇲🇽" },
  INR: { name: "India", flag: "🇮🇳" },
  RUB: { name: "Russia", flag: "🇷🇺" },
  ZAR: { name: "South Africa", flag: "🇿🇦" },
  TRY: { name: "Turkey", flag: "🇹🇷" },
  BRL: { name: "Brazil", flag: "🇧🇷" },
  TWD: { name: "Taiwan", flag: "🇹🇼" },
  DKK: { name: "Denmark", flag: "🇩🇰" },
  PLN: { name: "Poland", flag: "🇵🇱" },
  THB: { name: "Thailand", flag: "🇹🇭" },
  IDR: { name: "Indonesia", flag: "🇮🇩" },
  HUF: { name: "Hungary", flag: "🇭🇺" },
  CZK: { name: "Czech Republic", flag: "🇨🇿" },
  ILS: { name: "Israel", flag: "🇮🇱" },
  CLP: { name: "Chile", flag: "🇨🇱" },
  PHP: { name: "Philippines", flag: "🇵🇭" },
  AED: { name: "UAE", flag: "🇦🇪" },
  SAR: { name: "Saudi Arabia", flag: "🇸🇦" },
  MYR: { name: "Malaysia", flag: "🇲🇾" },
  RON: { name: "Romania", flag: "🇷🇴" },
  ARS: { name: "Argentina", flag: "🇦🇷" },
  VND: { name: "Vietnam", flag: "🇻🇳" },
  PKR: { name: "Pakistan", flag: "🇵🇰" },
  BDT: { name: "Bangladesh", flag: "🇧🇩" },
  EGP: { name: "Egypt", flag: "🇪🇬" },
  NGN: { name: "Nigeria", flag: "🇳🇬" },
  UAH: { name: "Ukraine", flag: "🇺🇦" },
  KZT: { name: "Kazakhstan", flag: "🇰🇿" },
  QAR: { name: "Qatar", flag: "🇶🇦" },
  KWD: { name: "Kuwait", flag: "🇰🇼" },
  OMR: { name: "Oman", flag: "🇴🇲" },
  BHD: { name: "Bahrain", flag: "🇧🇭" },
  JOD: { name: "Jordan", flag: "🇯🇴" },
  LKR: { name: "Sri Lanka", flag: "🇱🇰" },
  NPR: { name: "Nepal", flag: "🇳🇵" },
  ISK: { name: "Iceland", flag: "🇮🇸" },
  HRK: { name: "Croatia", flag: "🇭🇷" },
  BGN: { name: "Bulgaria", flag: "🇧🇬" },
  COP: { name: "Colombia", flag: "🇨🇴" },
  PEN: { name: "Peru", flag: "🇵🇪" },
  MAD: { name: "Morocco", flag: "🇲🇦" },
  KES: { name: "Kenya", flag: "🇰🇪" },
  UYU: { name: "Uruguay", flag: "🇺🇾" },
  GHS: { name: "Ghana", flag: "🇬🇭" },
  TZS: { name: "Tanzania", flag: "🇹🇿" },
  UGX: { name: "Uganda", flag: "🇺🇬" },
};

const getCurrencyDisplay = (code) => {
  const data = CURRENCY_DATA[code];
  if (data) {
    return `${data.flag} ${data.name} (${code})`;
  }
  return code;
};

function HistoryItem({ item, getRateChange, handleExpandChart, CustomTooltip }) {
  const [rateData, setRateData] = useState(null);
  const daysDiff = Math.floor((new Date() - new Date(item.timestamp)) / (1000 * 60 * 60 * 24));
  
  useEffect(() => {
    if (daysDiff <= 7) {
      getRateChange(item).then(data => setRateData(data));
    }
  }, [item, daysDiff]);
  
  return (
    <div
      className="history-item p-2.5 rounded-lg border border-border"
      data-testid={`history-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs font-semibold">{item.from_currency}</span>
          <span className="text-muted-foreground text-xs">→</span>
          <span className="text-xs font-semibold">{item.to_currency}</span>
        </div>
        <TrendingUp className="w-3 h-3 text-primary flex-shrink-0 mt-0.5" />
      </div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-xs text-muted-foreground">{item.amount}</span>
        <span className="text-xs text-muted-foreground">=</span>
        <span className="text-sm font-semibold">{item.converted_amount.toFixed(2)}</span>
      </div>
      
      {/* 7-Day Average Rate Chart */}
      {daysDiff <= 7 && rateData && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">7-Day Trend</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-primary">
                Avg: {rateData.avgRate.toFixed(4)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 opacity-70 hover:opacity-100"
                onClick={() => handleExpandChart(item)}
              >
                <Maximize2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          
          {/* Mini Chart with Tooltip */}
          <div className="h-8 w-full mt-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rateData.sevenDayData}>
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="rate" 
                  stroke="#5BD88C" 
                  strokeWidth={1.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div className="flex justify-between text-xs text-muted-foreground/60 mt-1">
            <span>{rateData.sevenDayData[0]?.date}</span>
            <span>{rateData.sevenDayData[rateData.sevenDayData.length - 1]?.date}</span>
          </div>
        </div>
      )}
      
      <p className="text-xs text-muted-foreground/60 mt-1">
        {new Date(item.timestamp).toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })}
        {daysDiff <= 7 && daysDiff > 0 && (
          <span className="ml-1">• {daysDiff}d ago</span>
        )}
      </p>
    </div>
  );
}

function HomePage() {
  const [currencies, setCurrencies] = useState([]);
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("EUR");
  const [amount, setAmount] = useState(1);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [rates, setRates] = useState({});
  const [expandedChart, setExpandedChart] = useState(null);
  const [historicalData, setHistoricalData] = useState({});

  // Prepare currency options for SearchableSelect
  const currencyOptions = currencies.map(code => ({
    code,
    name: CURRENCY_DATA[code]?.name || code,
    flag: CURRENCY_DATA[code]?.flag || ""
  }));

  useEffect(() => {
    fetchCurrencies();
    fetchHistory();
    fetchFavorites();
  }, []);

  useEffect(() => {
    if (fromCurrency) {
      fetchRates(fromCurrency);
    }
  }, [fromCurrency]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      toast.error("Failed to fetch currencies");
    }
  };

  const fetchRates = async (base) => {
    try {
      const response = await axios.get(`${API}/rates/${base}`);
      setRates(response.data.rates);
    } catch (error) {
      console.error("Failed to fetch rates", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history", error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error("Failed to fetch favorites", error);
    }
  };

  const fetchHistoricalRates = async (fromCurrency, toCurrency, days = 7) => {
    const cacheKey = `${fromCurrency}-${toCurrency}-${days}`;
    
    // Check if already cached
    if (historicalData[cacheKey]) {
      return historicalData[cacheKey];
    }

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const formatDate = (date) => date.toISOString().split('T')[0];
      
      // Using Frankfurter API for historical rates (free, no API key needed)
      const response = await axios.get(
        `https://api.frankfurter.app/${formatDate(startDate)}..${formatDate(endDate)}?from=${fromCurrency}&to=${toCurrency}`
      );

      const rates = response.data.rates;
      const chartData = Object.entries(rates).map(([date, rateObj]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: date,
        rate: rateObj[toCurrency]
      }));

      // Cache the data
      setHistoricalData(prev => ({ ...prev, [cacheKey]: chartData }));
      return chartData;
    } catch (error) {
      console.error("Failed to fetch historical rates", error);
      return null;
    }
  };

  const handleConvert = async () => {
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/convert`, {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount: parseFloat(amount),
      });

      setConvertedAmount(response.data.converted_amount);
      setExchangeRate(response.data.exchange_rate);
      toast.success("Conversion successful!");
      fetchHistory();
    } catch (error) {
      toast.error("Conversion failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  };

  const handleAddFavorite = async () => {
    try {
      await axios.post(`${API}/favorites`, {
        from_currency: fromCurrency,
        to_currency: toCurrency,
      });
      toast.success("Added to favorites!");
      fetchFavorites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add favorite");
    }
  };

  const handleRemoveFavorite = async (id) => {
    try {
      await axios.delete(`${API}/favorites/${id}`);
      toast.success("Removed from favorites");
      fetchFavorites();
    } catch (error) {
      toast.error("Failed to remove favorite");
    }
  };

  const handleUseFavorite = (fav) => {
    setFromCurrency(fav.from_currency);
    setToCurrency(fav.to_currency);
    toast.success(`Loaded ${fav.from_currency} → ${fav.to_currency}`);
  };

  const getLiveRate = (from, to) => {
    if (rates && rates[to] && from === fromCurrency) {
      return rates[to];
    }
    return null;
  };

  const handleExpandChart = async (item) => {
    const data30Days = await fetchHistoricalRates(item.from_currency, item.to_currency, 30);
    setExpandedChart({
      ...item,
      chartData: data30Days
    });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
          <p className="text-xs text-muted-foreground">{payload[0].payload.fullDate || payload[0].payload.date}</p>
          <p className="text-sm font-semibold text-primary">
            Rate: {payload[0].value.toFixed(4)}
          </p>
        </div>
      );
    }
    return null;
  };

  const getRateChange = async (item) => {
    // Fetch real historical data from Frankfurter API
    const chartData = await fetchHistoricalRates(item.from_currency, item.to_currency, 7);
    
    if (!chartData || chartData.length === 0) return null;
    
    // Calculate 7-day average
    const avgRate = chartData.reduce((sum, d) => sum + d.rate, 0) / chartData.length;
    
    return {
      sevenDayData: chartData,
      avgRate: avgRate,
      currentRate: chartData[chartData.length - 1]?.rate
    };
  };

  return (
    <>
      <div className="App">
        <Toaster position="top-center" />
        <div className="app-background"></div>
        <div className="content-wrapper">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-12">
          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-2" style={{ fontFamily: 'Manrope' }}>
              MudraFX
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans' }}>
              Real-time rates for 150+ currencies
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 lg:gap-8">
            {/* Left: Favorites */}
            <div className="lg:col-span-3 order-2 lg:order-1" data-testid="favorites-section">
              <Card className="card-hover border-border">
                <CardHeader className="pb-3 pt-4 px-4">
                  <CardTitle className="flex items-center justify-between text-base md:text-lg" style={{ fontFamily: 'Manrope' }}>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-accent" fill="currentColor" />
                      <span>Quick Access</span>
                    </div>
                    {favorites.length > 0 && (
                      <span className="text-xs font-normal text-muted-foreground">{favorites.length}</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  {favorites.length === 0 ? (
                    <div className="text-center py-6 px-2" data-testid="no-favorites-message">
                      <div className="mb-3 opacity-40">
                        <Star className="w-12 h-12 mx-auto text-accent" strokeWidth={1} />
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">No favorites yet</p>
                      <p className="text-xs text-muted-foreground/60">Tap ⭐ to save pairs</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {favorites.map((fav) => {
                        const liveRate = getLiveRate(fav.from_currency, fav.to_currency);
                        return (
                          <div
                            key={fav.id}
                            className="favorite-item group flex items-center justify-between p-2.5 rounded-lg border border-border cursor-pointer hover:border-accent/50"
                            onClick={() => handleUseFavorite(fav)}
                            data-testid={`favorite-item-${fav.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-sm font-semibold">{fav.from_currency}</span>
                                <span className="text-muted-foreground text-xs">→</span>
                                <span className="text-sm font-semibold">{fav.to_currency}</span>
                              </div>
                              {liveRate && (
                                <p className="text-xs text-muted-foreground/80">
                                  1 = {liveRate.toFixed(4)}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFavorite(fav.id);
                              }}
                              data-testid={`remove-favorite-${fav.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Center: Main Converter */}
            <div className="lg:col-span-6 order-1 lg:order-2" data-testid="converter-section">
              <Card className="card-hover border-border shadow-lg">
                <CardHeader className="text-center pb-4 md:pb-6">
                  <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold text-[#E5B33E]" style={{ fontFamily: 'Manrope' }}>
                    Convert Currency
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-center">Enter amount and select currencies</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 md:space-y-6">
                  {/* Amount Input */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-2 font-medium text-muted-foreground">
                      Amount
                    </label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="text-base md:text-lg h-11 md:h-12 currency-input bg-input/50 border-transparent focus:border-primary"
                      data-testid="amount-input"
                    />
                  </div>

                  {/* From Currency */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-2 font-medium text-muted-foreground">
                      From
                    </label>
                    <SearchableSelect
                      value={fromCurrency}
                      onValueChange={setFromCurrency}
                      options={currencyOptions}
                      placeholder="Select currency..."
                      testId="from-currency-select"
                    />
                  </div>

                  {/* Swap Button */}
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleSwap}
                      className="rounded-full h-10 w-10 md:h-12 md:w-12 btn-primary"
                      data-testid="swap-button"
                    >
                      <ArrowRightLeft className="w-4 h-4 md:w-5 md:h-5" />
                    </Button>
                  </div>

                  {/* To Currency */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest mb-2 font-medium text-muted-foreground">
                      To
                    </label>
                    <SearchableSelect
                      value={toCurrency}
                      onValueChange={setToCurrency}
                      options={currencyOptions}
                      placeholder="Select currency..."
                      testId="to-currency-select"
                    />
                  </div>

                  {/* Convert Button */}
                  <Button
                    onClick={handleConvert}
                    disabled={loading}
                    className="w-full h-11 md:h-12 text-base md:text-lg rounded-full bg-primary hover:bg-primary/90 btn-primary"
                    data-testid="convert-button"
                  >
                    {loading ? "Converting..." : "Convert"}
                  </Button>

                  {/* Result */}
                  {convertedAmount !== null && (
                    <Card className="bg-muted border-border" data-testid="conversion-result">
                      <CardContent className="pt-4 md:pt-6">
                        <p className="text-xs md:text-sm text-muted-foreground mb-1">
                          {amount} {fromCurrency} =
                        </p>
                        <p className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>
                          {convertedAmount.toFixed(2)} {toCurrency}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Rate: 1 {fromCurrency} = {exchangeRate?.toFixed(4)} {toCurrency}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Add to Favorites */}
                  <Button
                    variant="outline"
                    onClick={handleAddFavorite}
                    className="w-full h-10 md:h-11 text-sm md:text-base"
                    data-testid="add-favorite-button"
                  >
                    <Star className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                    Add to Favorites
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Right: History */}
            <div className="lg:col-span-3 order-3" data-testid="history-section">
              <Card className="card-hover border-border">
                <CardHeader className="pb-3 pt-4 px-4">
                  <CardTitle className="flex items-center justify-between text-base md:text-lg" style={{ fontFamily: 'Manrope' }}>
                    <div className="flex items-center gap-2">
                      <History className="w-4 h-4 text-primary" />
                      <span>Recent</span>
                    </div>
                    {history.length > 0 && (
                      <span className="text-xs font-normal text-muted-foreground">{history.length}</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  {history.length === 0 ? (
                    <div className="text-center py-6 px-2" data-testid="no-history-message">
                      <div className="mb-3 opacity-40">
                        <TrendingUp className="w-12 h-12 mx-auto text-primary" strokeWidth={1} />
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">No history yet</p>
                      <p className="text-xs text-muted-foreground/60">Start converting</p>
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[280px]">
                      <div className="space-y-1.5 pr-3">
                        {history.slice(0, 10).map((item, idx) => (
                          <HistoryItem 
                            key={item.id || idx} 
                            item={item} 
                            getRateChange={getRateChange}
                            handleExpandChart={handleExpandChart}
                            CustomTooltip={CustomTooltip}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Expanded Chart Modal */}
    <Dialog open={!!expandedChart} onOpenChange={() => setExpandedChart(null)}>
        <DialogContent className="max-w-4xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-xl" style={{ fontFamily: 'Manrope' }}>
              <span className="text-yellow-500">
                {expandedChart?.from_currency} → {expandedChart?.to_currency} (30-Day Trend)
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setExpandedChart(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {expandedChart?.chartData && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Your Conversion</p>
                  <p className="text-lg font-semibold">
                    {expandedChart.amount} = {expandedChart.converted_amount.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">30-Day Average</p>
                  <p className="text-lg font-semibold text-primary">
                    {(expandedChart.chartData.reduce((sum, d) => sum + d.rate, 0) / expandedChart.chartData.length).toFixed(4)}
                  </p>
                </div>
              </div>
              
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={expandedChart.chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                    <XAxis 
                      dataKey="date" 
                      stroke="#96FF96" 
                      style={{ fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      stroke="#96FF96" 
                      style={{ fontSize: '12px' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="rate" 
                      stroke="#5BD88C" 
                      strokeWidth={2}
                      dot={{ fill: '#5BD88C', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground">Highest</p>
                  <p className="text-sm font-semibold text-primary">
                    {Math.max(...expandedChart.chartData.map(d => d.rate)).toFixed(4)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Lowest</p>
                  <p className="text-sm font-semibold text-destructive">
                    {Math.min(...expandedChart.chartData.map(d => d.rate)).toFixed(4)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Volatility</p>
                  <p className="text-sm font-semibold">
                    {((Math.max(...expandedChart.chartData.map(d => d.rate)) - Math.min(...expandedChart.chartData.map(d => d.rate))) / Math.min(...expandedChart.chartData.map(d => d.rate)) * 100).toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default HomePage;